/**
 * useTTS Hook
 * Handles Text-to-Speech functionality with multi-language support
 * Uses Google Cloud Text-to-Speech API for reliable multi-language support
 */

import { useState, useRef, useCallback } from 'react';
import { logger } from '../utils/logger.js';

// Language code mapping for Google Cloud TTS
const LANGUAGE_CODE_MAP = {
  'US': 'en-US',
  'NP': 'ne-IN', // Nepali uses Indian English voice
  'IN': 'hi-IN',
  'FR': 'fr-FR',
  'ES': 'es-ES',
  'IT': 'it-IT',
  'JP': 'ja-JP',
  'CN': 'zh-CN',
  'BD': 'bn-IN',
};

// Voice names for better quality
const VOICE_NAMES = {
  'en-US': 'en-US-Neural2-C',
  'ne-IN': 'hi-IN-Neural2-A', // Fallback to Hindi for Nepali
  'hi-IN': 'hi-IN-Neural2-A',
  'fr-FR': 'fr-FR-Neural2-A',
  'es-ES': 'es-ES-Neural2-A',
  'it-IT': 'it-IT-Neural2-A',
  'ja-JP': 'ja-JP-Neural2-B',
  'zh-CN': 'zh-CN-Neural2-A',
  'bn-IN': 'bn-IN-Standard-A',
};

export const useTTS = () => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);
  const currentLanguageRef = useRef('US');
  const currentTextRef = useRef('');

  // Split long text into chunks for API
  const splitTextIntoChunks = useCallback((text, chunkSize = 4500) => {
    const chunks = [];
    let currentChunk = '';

    // Split by sentences to maintain readability
    const sentences = text.split(/(?<=[।.!?])\s+/);

    for (const sentence of sentences) {
      if ((currentChunk + sentence).length > chunkSize) {
        if (currentChunk) chunks.push(currentChunk.trim());
        currentChunk = sentence;
      } else {
        currentChunk += (currentChunk ? ' ' : '') + sentence;
      }
    }

    if (currentChunk) chunks.push(currentChunk.trim());
    return chunks.length > 0 ? chunks : [text];
  }, []);

  const synthesizeText = useCallback(async (text, languageCode) => {
    try {
      const apiKey = import.meta.env.VITE_GOOGLE_CLOUD_TTS_API_KEY;
      
      if (!apiKey) {
        logger.warn('Google Cloud TTS API key not configured, using Web Speech API fallback');
        return null;
      }

      const langCode = LANGUAGE_CODE_MAP[languageCode] || 'en-US';
      const voiceName = VOICE_NAMES[langCode] || 'en-US-Neural2-C';

      const response = await fetch(
        `https://texttospeech.googleapis.com/v1/text:synthesize?key=${apiKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            input: { text },
            voice: {
              languageCode: langCode,
              name: voiceName,
            },
            audioConfig: {
              audioEncoding: 'MP3',
              pitch: 0,
              speakingRate: 1,
            },
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      return data.audioContent; // Base64 encoded audio
    } catch (err) {
      logger.error('Failed to synthesize speech', err);
      setError(err.message);
      return null;
    }
  }, []);

  const playAudio = useCallback((audioContent) => {
    if (!audioContent) return;

    try {
      const audioBlob = new Blob(
        [Uint8Array.from(atob(audioContent), c => c.charCodeAt(0))],
        { type: 'audio/mp3' }
      );
      const audioUrl = URL.createObjectURL(audioBlob);

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }

      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onplay = () => {
        setIsSpeaking(true);
        setIsPaused(false);
        setError(null);
      };

      audio.onpause = () => {
        setIsPaused(true);
      };

      audio.onended = () => {
        setIsSpeaking(false);
        setIsPaused(false);
        URL.revokeObjectURL(audioUrl);
      };

      audio.onerror = (err) => {
        logger.error('Audio playback failed', err);
        setError('Failed to play audio');
        setIsSpeaking(false);
        setIsPaused(false);
      };

      audio.play().catch(err => {
        logger.error('Failed to play audio', err);
        setError('Failed to play audio');
      });
    } catch (err) {
      logger.error('Audio setup failed', err);
      setError('Failed to setup audio');
    }
  }, []);

  const speak = useCallback(async (text, languageCode = 'US') => {
    if (!text || text.trim().length === 0) return;

    // Stop any current playback
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }

    currentTextRef.current = text;
    currentLanguageRef.current = languageCode;

    try {
      setError(null);
      const chunks = splitTextIntoChunks(text);

      if (chunks.length === 0) return;

      // For now, synthesize and play the first chunk
      // In production, you might want to queue all chunks
      const audioContent = await synthesizeText(chunks[0], languageCode);

      if (audioContent) {
        playAudio(audioContent);
      } else {
        // Fallback to Web Speech API
        fallbackToWebSpeech(text, languageCode);
      }
    } catch (err) {
      console.error('Speak error:', err);
      setError('Failed to synthesize speech');
      fallbackToWebSpeech(text, languageCode);
    }
  }, [splitTextIntoChunks, synthesizeText, playAudio]);

  const fallbackToWebSpeech = useCallback((text, languageCode) => {
    const LANGUAGE_CODE_MAP_WEB = {
      'US': 'en-US',
      'NP': 'ne-NP',
      'IN': 'hi-IN',
      'FR': 'fr-FR',
      'ES': 'es-ES',
      'IT': 'it-IT',
      'JP': 'ja-JP',
      'CN': 'zh-CN',
      'BD': 'bn-IN',
    };

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = LANGUAGE_CODE_MAP_WEB[languageCode] || 'en-US';
    utterance.rate = 1;
    utterance.pitch = 1;
    utterance.volume = 1;

    utterance.onstart = () => {
      setIsSpeaking(true);
      setIsPaused(false);
    };

    utterance.onend = () => {
      setIsSpeaking(false);
      setIsPaused(false);
    };

    utterance.onerror = (event) => {
      logger.error('Web Speech synthesis failed', event.error);
      setError(`Speech error: ${event.error}`);
      setIsSpeaking(false);
      setIsPaused(false);
    };

    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  }, []);

  const pause = useCallback(() => {
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setIsPaused(true);
    } else if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
      window.speechSynthesis.pause();
      setIsPaused(true);
    }
  }, []);

  const resume = useCallback(() => {
    if (audioRef.current && audioRef.current.paused) {
      audioRef.current.play().catch(err => console.error('Resume error:', err));
      setIsPaused(false);
    } else if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
      setIsPaused(false);
    }
  }, []);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
    setIsPaused(false);
    setError(null);
  }, []);

  return {
    speak,
    pause,
    resume,
    stop,
    isSpeaking,
    isPaused,
    error,
  };
};
